import { find, values } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import LoadingIcon from "../../../../components/icons/LoadingIcon";
import PlusIcon from "../../../../components/icons/PlusIcon";
import Labels from "../../../../components/labels/Labels";
import { getLabelColorStyle } from "../../../../components/labels/utils";
import { useAppearance } from "../../../../context/appearance";
import { useAuthentication } from "../../../../context/authentication";
import { useWallets } from "../../../../context/wallets";
import { Entity } from "../../../../utils/constants";
import { PhpHotspot } from "../../../../utils/types";
import { LabelFilterKey } from "../../../../utils/useFilteredLabels";

interface Props {
  hotspot: PhpHotspot;
}

const HotspotWalletLabel: React.FC<Props> = ({ hotspot }) => {
  const { wrapActionWithAuth } = useAuthentication();
  const { isDark } = useAppearance();
  const { wallets, addWallet } = useWallets();
  const [addingWallet, setAddingWallet] = useState(false);

  const wallet = useMemo(
    () => find(values(wallets), { address: hotspot.owner }),
    [hotspot.owner, wallets]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleAddWallet = useCallback(
    wrapActionWithAuth(async () => {
      setAddingWallet(true);
      await addWallet(hotspot.owner, { tracked: false });
      setAddingWallet(false);
    }),
    [wrapActionWithAuth, hotspot, addWallet]
  );

  const renderLabels = useMemo(() => {
    if (!wallet) return null;

    return (
      <Labels
        entity={Entity.wallet}
        linkedUuid={wallet.uuid}
        filterKey={LabelFilterKey.mapHotspots}
      />
    );
  }, [wallet]);

  if (wallet) {
    return renderLabels;
  }

  return (
    <button
      type="button"
      disabled={addingWallet}
      onClick={handleAddWallet}
      className="inline-flex items-center rounded-full font-medium px-3 py-0.5 my-2 text-sm cursor-pointer disabled:cursor-default"
      style={getLabelColorStyle("white", isDark)}
    >
      {addingWallet ? (
        <>
          <LoadingIcon className="animate-spin mr-2 h-4 w-4" />
          <span>Adding wallet</span>
        </>
      ) : (
        <>
          <PlusIcon className="-ml-1 mr-0.5 h-5 w-5" />
          <span>Add wallet label</span>
        </>
      )}
    </button>
  );
};

export default HotspotWalletLabel;
